<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div 
        v-if="isMenuOpen" 
        class="mobile-menu-overlay md:hidden"
        @click="closeMenu"
      ></div>
    </transition>

    <!-- Slide-in Menu -->
    <transition name="slide">
      <nav v-if="isMenuOpen" class="mobile-slide-menu md:hidden">
        <!-- Header -->
        <div class="menu-header">
          <CompanyLogo position="sidebar" class="menu-logo" />
          <button @click="closeMenu" class="close-btn" aria-label="Close menu">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <!-- User Info -->
        <div v-if="user" class="user-info">
          <div class="user-avatar">
            <img 
              v-if="user.profile_image" 
              :src="user.profile_image" 
              :alt="userFullName"
            />
            <fa-icon v-else :icon="['fas', 'user-circle']" class="default-avatar" />
          </div>
          <div class="user-details">
            <h3>{{ userFullName }}</h3>
            <p>{{ user.email }}</p>
          </div>
        </div>

        <!-- Menu Items -->
        <ul class="menu-list">
          <li>
            <router-link 
              :to="{ name: 'dashboard' }" 
              class="menu-item"
              active-class="menu-item--active"
              exact
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'home']" class="menu-icon" />
              <span>Dashboard</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'tags' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'tags']" class="menu-icon" />
              <span>Tags</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'ads' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'file-alt']" class="menu-icon" />
              <span>Ads.txt</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'reports' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'chart-pie']" class="menu-icon" />
              <span>Reports</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'request_new_format' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'plus-square']" class="menu-icon" />
              <span>Request New Format</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'report_ad' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'comment-dots']" class="menu-icon" />
              <span>Report Ad</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'payments' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'credit-card']" class="menu-icon" />
              <span>Payments</span>
            </router-link>
          </li>
          <li>
            <router-link 
              :to="{ name: 'profile' }" 
              class="menu-item"
              active-class="menu-item--active"
              @click.native="closeMenu"
            >
              <fa-icon :icon="['fas', 'user']" class="menu-icon" />
              <span>Profile</span>
            </router-link>
          </li>

          <template v-if="$store.getters.isMainLabel">
            <li class="menu-divider"></li>
            <li>
              <a 
                href="http://www.adcrew.co/outstream/" 
                target="_blank" 
                class="menu-item"
                @click="closeMenu"
              >
                <fa-icon :icon="['fas', 'video']" class="menu-icon" />
                <span>Discover OutStream</span>
              </a>
            </li>
            <li>
              <a 
                href="mailto:support@adcrew.co" 
                target="_blank" 
                class="menu-item"
                @click="closeMenu"
              >
                <fa-icon :icon="['fas', 'question-circle']" class="menu-icon" />
                <span>Support</span>
              </a>
            </li>
          </template>

          <li class="menu-divider"></li>
          <li>
            <a 
              href="#" 
              @click.prevent="logout" 
              class="menu-item menu-item--logout"
            >
              <fa-icon :icon="['fas', 'sign-out-alt']" class="menu-icon" />
              <span>Logout</span>
            </a>
          </li>
        </ul>
      </nav>
    </transition>
  </div>
</template>

<script>
import CompanyLogo from '@/components/core/CompanyLogo'

export default {
  name: 'SidebarMobile',
  components: {
    CompanyLogo
  },
  props: {
    user: {
      type: Object,
      default: () => ({})
    },
  },
  data() {
    return {
      isMenuOpen: false,
      scrollPosition: 0
    }
  },
  computed: {
    userFullName() {
      if (!this.user) return '';
      return `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim();
    }
  },
  mounted() {
    // Listen for global menu toggle event
    this.$root.$on('toggle-mobile-menu', this.toggleMenu);
  },
  beforeDestroy() {
    this.$root.$off('toggle-mobile-menu', this.toggleMenu);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      // Safari-compatible body scroll prevention
      if (this.isMenuOpen) {
        this.scrollPosition = window.pageYOffset;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.width = '100%';
      } else {
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('width');
        window.scrollTo(0, this.scrollPosition);
      }
    },
    closeMenu() {
      this.isMenuOpen = false;
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      window.scrollTo(0, this.scrollPosition);
    },
    logout() {
      this.closeMenu();
      this.$store.dispatch('AUTH_LOGOUT').then(() => {
        this.$router.push('/login');
      });
    }
  }
}
</script>

<style scoped>
/* Overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  -webkit-tap-highlight-color: transparent;
}

/* Slide Menu */
.mobile-slide-menu {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  max-width: 85vw;
  background: #ffffff;
  z-index: 1100;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

/* Menu Header */
.menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
}

.menu-logo {
  max-width: 140px;
  height: auto;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* User Info */
.user-info {
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-avatar {
  font-size: 32px;
  color: #9ca3af;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-details h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-details p {
  margin: 2px 0 0 0;
  font-size: 13px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Menu List */
.menu-list {
  list-style: none;
  padding: 8px 0;
  margin: 0;
  flex: 1;
}

.menu-list li {
  margin: 0;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s ease;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.menu-item:hover {
  background: #f3f4f6;
  color: #1e40af;
}

.menu-item--active {
  background: #eff6ff;
  color: #1e40af;
  border-left: 3px solid #1e40af;
}

.menu-item--logout {
  color: #ef4444;
}

.menu-item--logout:hover {
  background: #fee2e2;
  color: #dc2626;
}

.menu-icon {
  width: 20px;
  font-size: 18px;
  flex-shrink: 0;
}

.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 8px 20px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active {
  transition: transform 0.3s ease-out;
}

.slide-leave-active {
  transition: transform 0.25s ease-in;
}

.slide-enter {
  transform: translateX(-100%);
}

.slide-leave-to {
  transform: translateX(-100%);
}

/* Dark Mode */
:global(.dark) .mobile-slide-menu {
  background: #1f2937;
}

:global(.dark) .menu-header {
  border-bottom-color: #374151;
}

:global(.dark) .user-info {
  background: #111827;
  border-bottom-color: #374151;
}

:global(.dark) .user-details h3 {
  color: #f9fafb;
}

:global(.dark) .user-details p {
  color: #9ca3af;
}

:global(.dark) .menu-item {
  color: #e5e7eb;
}

:global(.dark) .menu-item:hover {
  background: #374151;
  color: #60a5fa;
}

:global(.dark) .menu-item--active {
  background: #1e3a5f;
  color: #60a5fa;
  border-left-color: #60a5fa;
}

:global(.dark) .menu-divider {
  background: #374151;
}

/* Scrollbar */
.mobile-slide-menu::-webkit-scrollbar {
  width: 6px;
}

.mobile-slide-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.mobile-slide-menu::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.mobile-slide-menu::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>